import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'

import { Order, OrderItem } from '@/api/admin-orders-types'
import { useRefundOrder } from '@/api/api-mutations'
import { useGetInventorySettings } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Checkbox } from '@/components/base/checkbox'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { usePrompt } from '@/components/base/prompt'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { MinusMini } from '@/icons/minus-mini'
import { PlusMini } from '@/icons/plus-mini'
import { clx } from '@/utils/clx'
import { add, divide, largerThan, multiply, PrecisionAmount, subtract, sum, toPrecision } from '@/utils/currency'

export const Route = createFileRoute('/_authenticated/_general/orders/$orderUuid/refund')({
	component: RouteComponent,
	staticData: { crumb: ({ context: { L } }) => L.Orders.Refund.Title() }
})

function RouteComponent() {
	const { orderUuid } = Route.useParams()
	return (
		<RouteModal prev='..' asDrawer requiresData loader={api => api.getOrder({ path: { orderUuid } })}>
			{order => <OrderRefundForm order={order} />}
		</RouteModal>
	)
}

const ZERO = toPrecision(0, 2)

type RefundSelection = Record<string, number>

/** Gross amount to refund for `quantity` units of a line, matching the server's per-unit derivation. */
const lineRefundAmount = (item: OrderItem, quantity: number): PrecisionAmount => {
	const lineQuantity = item.quantityInfo.quantity
	if (lineQuantity <= 0) {
		return ZERO
	}
	if (quantity >= lineQuantity) {
		return item.totalPrice
	}
	return multiply(divide(item.totalPrice, lineQuantity), quantity)
}

const itemsTotalFor = (selection: RefundSelection, items: OrderItem[]): PrecisionAmount => {
	const amounts = Object.entries(selection)
		.filter(([, quantity]) => quantity > 0)
		.map(([uuid, quantity]) => {
			const item = items.find(i => i.uuid === uuid)
			return item ? lineRefundAmount(item, quantity) : ZERO
		})
	return amounts.length > 0 ? sum(amounts) : ZERO
}

const totalFor = (
	selection: RefundSelection,
	customAmount: PrecisionAmount | null,
	items: OrderItem[]
): PrecisionAmount => {
	const itemsTotal = itemsTotalFor(selection, items)
	return customAmount ? add(itemsTotal, customAmount) : itemsTotal
}

function OrderRefundForm({ order }: { order: Order }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const refundOrder = useRefundOrder()
	const prompt = usePrompt()

	const inventorySettings = useGetInventorySettings()
	// Restock choice is only relevant when inventory tracking is on. Seed each line's checkbox from the
	// channel's restock-on-return default so merchants only toggle it off for damaged/non-resalable goods.
	const inventoryEnabled = inventorySettings.data?.enabled ?? false
	const restockDefault = inventorySettings.data?.restockOnReturn ?? false

	const currencyCode = order.currencyCode
	const refundableAmount = order.refundableAmount ?? ZERO

	const items = useMemo(() => order.itemGroups.flatMap(group => group.items), [order.itemGroups])
	const refundableItems = items.filter(item => (item.refundableQuantity ?? 0) > 0 && !item.promotionLocked)
	const promotionLockedItems = items.filter(item => item.promotionLocked && (item.refundableQuantity ?? 0) > 0)

	// Cross-field validation drives SaveButton's disabled state (form.isValid). Runs on mount too so
	// the button starts disabled until the user selects items or enters a valid custom amount.
	const validateRefund = ({
		value
	}: {
		value: { selection: RefundSelection; customAmount: PrecisionAmount | null; note: string }
	}) => {
		const hasSelection = Object.values(value.selection).some(quantity => quantity > 0)
		const hasCustomAmount = !!value.customAmount && largerThan(value.customAmount, 0)
		const total = totalFor(value.selection, value.customAmount, items)
		if ((!hasSelection && !hasCustomAmount) || !largerThan(total, 0)) {
			return L.Orders.Refund.SelectSomething()
		}
		if (largerThan(total, refundableAmount)) {
			return L.Orders.Refund.ExceedsRefundable()
		}
		return undefined
	}

	const form = useAppForm({
		defaultValues: {
			selection: {} as RefundSelection,
			restock: {} as Record<string, boolean>,
			customAmount: null as PrecisionAmount | null,
			note: ''
		},
		validators: {
			onMount: validateRefund,
			onChange: validateRefund
		},
		onSubmit: async ({ value }) => {
			const selectedEntries = Object.entries(value.selection).filter(([, quantity]) => quantity > 0)
			const hasSelection = selectedEntries.length > 0
			const hasCustomAmount = !!value.customAmount && largerThan(value.customAmount, 0)
			const note = value.note.trim() || undefined

			const total = totalFor(value.selection, value.customAmount, items)
			const confirmed = await prompt({
				title: L.Orders.Refund.ConfirmTitle(),
				description: L.Orders.Refund.ConfirmWarning({ total: { amount: total, currencyCode } }),
				confirmText: L.Orders.Refund.Submit(),
				cancelText: L.General.Cancel()
			})
			if (!confirmed) {
				return
			}

			const body = hasSelection
				? {
						type: 'ITEM_BASED' as const,
						items: selectedEntries.map(([orderItemUuid, quantity]) => ({
							orderItemUuid,
							quantity,
							restock: inventoryEnabled ? (value.restock[orderItemUuid] ?? restockDefault) : undefined
						})),
						additionalAmount: hasCustomAmount ? value.customAmount! : undefined,
						note
					}
				: {
						type: 'AMOUNT_ONLY' as const,
						amount: value.customAmount!,
						note
					}

			try {
				const result = await refundOrder({ path: { orderUuid: order.uuid }, body })
				if (result.isSuccess) {
					toast.success(L.Orders.Refund.Success())
					handleSuccess()
				} else {
					toast.error(L.Orders.Refund.Error())
				}
			} catch {
				toast.error(L.Orders.Refund.Error())
			}
		}
	})

	const nothingToRefund = !largerThan(refundableAmount, 0)

	const setItemQuantity = (selection: RefundSelection, item: OrderItem, quantity: number): RefundSelection => {
		const max = item.refundableQuantity ?? 0
		let clamped = Math.max(0, Math.min(quantity, max))
		// Never let the item selection exceed the order's refundable amount. After a prior
		// amount-only refund the per-unit line value can sum to more than what is actually still
		// refundable, so cap the quantity to whatever fits alongside the other selected items.
		const othersTotal = itemsTotalFor({ ...selection, [item.uuid]: 0 }, items)
		while (clamped > 0 && largerThan(add(othersTotal, lineRefundAmount(item, clamped)), refundableAmount)) {
			clamped--
		}
		const next = { ...selection }
		if (clamped <= 0) {
			delete next[item.uuid]
		} else {
			next[item.uuid] = clamped
		}
		return next
	}

	const setItemRestock = (item: OrderItem, checked: boolean) => {
		const current = form.getFieldValue('restock')
		form.setFieldValue('restock', { ...current, [item.uuid]: checked })
	}

	// Apply a new item selection, dropping any custom amount that would now push the total past the
	// refundable amount. Once the selected items cover the full refundable amount the custom amount
	// field is disabled, so it must not retain a stale value.
	const applySelection = (selection: RefundSelection) => {
		form.setFieldValue('selection', selection)
		if (!largerThan(subtract(refundableAmount, itemsTotalFor(selection, items)), 0)) {
			form.setFieldValue('customAmount', null)
		}
	}

	// Selection with every refundable line item at its maximum refundable quantity, capped by
	// setItemQuantity so the running total never exceeds the order's refundable amount.
	const buildFullSelection = (): RefundSelection =>
		refundableItems.reduce<RefundSelection>(
			(selection, item) => setItemQuantity(selection, item, item.refundableQuantity ?? 0),
			{}
		)

	// Fill the form so the refund total equals the full refundable amount: select as many line-item
	// units as fit (buildFullSelection is itself capped to the refundable amount), then put any
	// remainder (fees, promotion-locked amounts, or value not representable as whole units) into the
	// custom amount so the total lands exactly on the refundable amount.
	const handleRefundEntireOrder = () => {
		const fullSelection = buildFullSelection()
		const remainder = subtract(refundableAmount, itemsTotalFor(fullSelection, items))
		form.setFieldValue('selection', fullSelection)
		form.setFieldValue('customAmount', largerThan(remainder, 0) ? remainder : null)
	}

	return (
		<form.AppForm>
			<div className='flex h-full flex-col'>
				<Drawer.Header>
					<Drawer.Title asChild>
						<Heading>{L.Orders.Refund.Title()}</Heading>
					</Drawer.Title>
					<Drawer.Description>{L.Orders.Refund.Description()}</Drawer.Description>
				</Drawer.Header>

				<Drawer.Body className='flex-1 overflow-auto'>
					{nothingToRefund ? (
						<div className='flex h-full items-center justify-center'>
							<Text className='text-ui-fg-subtle'>{L.Orders.Refund.NothingToRefund()}</Text>
						</div>
					) : (
						<FormContainer>
							<Button
								type='button'
								variant='secondary'
								size='small'
								className='w-full'
								onClick={handleRefundEntireOrder}
							>
								{L.Orders.Refund.RefundEntireOrder({ amount: refundableAmount, currencyCode })}
							</Button>
							<FormFieldGroup>
								<Text size='small' weight='plus'>
									{L.Orders.Refund.ItemsSectionTitle()}
								</Text>
								{refundableItems.length === 0 && promotionLockedItems.length === 0 ? (
									<Text size='small' className='text-ui-fg-subtle'>
										{L.Orders.Refund.NoRefundableItems()}
									</Text>
								) : (
									<form.AppField name='selection'>
										{field => {
											const allSelected =
												refundableItems.length > 0 &&
												refundableItems.every(
													item =>
														(field.state.value[item.uuid] ?? 0) >=
														(item.refundableQuantity ?? 0)
												)
											const someSelected = refundableItems.some(
												item => (field.state.value[item.uuid] ?? 0) > 0
											)
											return (
												<div className='flex flex-col divide-y divide-dashed rounded-lg border'>
													{refundableItems.length > 0 && (
														<label className='flex cursor-pointer items-center gap-x-3 px-3 py-2'>
															<Checkbox
																checked={
																	allSelected
																		? true
																		: someSelected
																			? 'indeterminate'
																			: false
																}
																onCheckedChange={() =>
																	applySelection(
																		someSelected ? {} : buildFullSelection()
																	)
																}
															/>
															<Text size='small' weight='plus'>
																{L.Orders.Refund.SelectAll()}
															</Text>
														</label>
													)}
													<form.Subscribe selector={state => state.values.restock}>
														{restockMap =>
															refundableItems.map(item => (
																<RefundItemRow
																	key={item.uuid}
																	item={item}
																	quantity={field.state.value[item.uuid] ?? 0}
																	currencyCode={currencyCode}
																	showRestock={inventoryEnabled}
																	restockChecked={
																		restockMap[item.uuid] ?? restockDefault
																	}
																	onRestockChange={checked =>
																		setItemRestock(item, checked)
																	}
																	onToggle={checked =>
																		applySelection(
																			setItemQuantity(
																				field.state.value,
																				item,
																				checked
																					? (item.refundableQuantity ?? 0)
																					: 0
																			)
																		)
																	}
																	onQuantityChange={quantity =>
																		applySelection(
																			setItemQuantity(
																				field.state.value,
																				item,
																				quantity
																			)
																		)
																	}
																/>
															))
														}
													</form.Subscribe>
													{promotionLockedItems.map(item => (
														<RefundItemRow
															key={item.uuid}
															item={item}
															quantity={0}
															currencyCode={currencyCode}
															locked
														/>
													))}
												</div>
											)
										}}
									</form.AppField>
								)}
							</FormFieldGroup>

							<FormFieldGroup>
								<form.Subscribe selector={state => state.values.selection}>
									{selection => {
										const hasSelection = Object.values(selection).some(quantity => quantity > 0)
										// Items already cover the full refundable amount, so there is
										// nothing left to add on top.
										const noHeadroom = !largerThan(
											subtract(refundableAmount, itemsTotalFor(selection, items)),
											0
										)
										return (
											<form.AppField name='customAmount'>
												{field => (
													<field.CurrencyField
														label={
															hasSelection
																? L.Orders.Refund.CustomAmountSectionTitle()
																: L.Orders.Refund.CustomAmountOnlyLabel()
														}
														currencyCode={currencyCode}
														disabled={noHeadroom}
													/>
												)}
											</form.AppField>
										)
									}}
								</form.Subscribe>
							</FormFieldGroup>

							<FormFieldGroup>
								<form.AppField name='note'>
									{field => <field.TextAreaField label={L.Orders.Refund.Note()} optional rows={3} />}
								</form.AppField>
							</FormFieldGroup>
						</FormContainer>
					)}
				</Drawer.Body>

				<Drawer.Footer className='flex-col items-stretch gap-y-3'>
					<form.Subscribe
						selector={state => ({
							selection: state.values.selection,
							customAmount: state.values.customAmount
						})}
					>
						{({ selection, customAmount }) => {
							const total = totalFor(selection, customAmount, items)
							const exceedsRefundable = largerThan(total, refundableAmount)
							return (
								<div className='flex flex-col gap-y-1'>
									<div className='flex items-center justify-between'>
										<Text size='small' className='text-ui-fg-subtle'>
											{L.Orders.Refund.RefundableAmount()}
										</Text>
										<Text size='small'>
											{L.Amount.Default({ amount: refundableAmount, currencyCode })}
										</Text>
									</div>
									<div className='flex items-center justify-between'>
										<Text size='small' weight='plus'>
											{L.Orders.Refund.RefundTotal()}
										</Text>
										<Text
											size='small'
											weight='plus'
											className={clx({ 'text-ui-fg-error': exceedsRefundable })}
										>
											{L.Amount.Default({ amount: total, currencyCode })}
										</Text>
									</div>
									{exceedsRefundable && (
										<Text size='small' className='text-ui-fg-error'>
											{L.Orders.Refund.ExceedsRefundable()}
										</Text>
									)}
								</div>
							)
						}}
					</form.Subscribe>
					<div className='flex items-center justify-end gap-x-2'>
						<Drawer.Close asChild>
							<Button variant='secondary' size='small'>
								{L.General.Cancel()}
							</Button>
						</Drawer.Close>
						<form.SaveButton label={L.Orders.Refund.Submit()} />
					</div>
				</Drawer.Footer>
			</div>
		</form.AppForm>
	)
}

function RefundItemRow({
	item,
	quantity,
	currencyCode,
	locked = false,
	showRestock = false,
	restockChecked = false,
	onRestockChange,
	onToggle,
	onQuantityChange
}: {
	item: OrderItem
	quantity: number
	currencyCode: string
	locked?: boolean
	showRestock?: boolean
	restockChecked?: boolean
	onRestockChange?: (checked: boolean) => void
	onToggle?: (checked: boolean) => void
	onQuantityChange?: (quantity: number) => void
}) {
	const { L } = useI18n()
	const max = item.refundableQuantity ?? 0
	const refunded = item.refundedQuantity ?? 0
	const totalQuantity = item.quantityInfo.quantity
	const selected = quantity > 0
	const rowAmount = selected ? lineRefundAmount(item, quantity) : lineRefundAmount(item, max)

	return (
		<div className={clx('flex items-center gap-x-3 px-3 py-3', { 'opacity-60': locked })}>
			{!locked && (
				<Checkbox
					checked={selected}
					onCheckedChange={checked => onToggle?.(checked === true)}
					disabled={max <= 0}
				/>
			)}
			<div className='flex min-w-0 flex-1 flex-col'>
				<Text size='small' weight='plus' className='truncate'>
					{item.variantName}
				</Text>
				<Text size='small' className='text-ui-fg-subtle'>
					{L.Amount.Default({ amount: rowAmount, currencyCode })}
					{refunded > 0 &&
						` · ${L.Orders.Refund.AlreadyRefunded({ quantity: refunded, total: totalQuantity })}`}
				</Text>
				{locked && (
					<Text size='small' className='text-ui-fg-muted'>
						{L.Orders.Refund.PromotionLocked()}
					</Text>
				)}
				{!locked && selected && showRestock && (
					<label className='mt-1 flex cursor-pointer items-center gap-x-2'>
						<Checkbox
							checked={restockChecked}
							onCheckedChange={checked => onRestockChange?.(checked === true)}
						/>
						<Text size='small' className='text-ui-fg-subtle'>
							{L.Orders.Refund.Restock()}
						</Text>
					</label>
				)}
			</div>
			{!locked && selected && (
				<div className='flex items-center gap-x-1'>
					<IconButton
						size='small'
						variant='transparent'
						type='button'
						disabled={quantity <= 1}
						onClick={() => onQuantityChange?.(quantity - 1)}
					>
						<MinusMini />
					</IconButton>
					<Text size='small' className='w-6 text-center tabular-nums'>
						{quantity}
					</Text>
					<IconButton
						size='small'
						variant='transparent'
						type='button'
						disabled={quantity >= max}
						onClick={() => onQuantityChange?.(quantity + 1)}
					>
						<PlusMini />
					</IconButton>
					<Text size='small' className='text-ui-fg-muted'>
						/ {max}
					</Text>
				</div>
			)}
		</div>
	)
}
